import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { type Community } from "@entities";
import { communityRepo } from "@repositories";
import { communityJoinUrl } from "@shared/helpers/constants";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type FC, useEffect, useMemo, useState } from "react";
import * as yup from "yup";
import { useAlert } from "../../../contexts/AlertProvider";
import { useOrganization } from "../../../contexts/OrganizationProvider";
import { getParentCommunityOptions } from "../../../helpers/community-parent-options";
import FormProvider, { useForm } from "../../../helpers/forms/FormProvider";
import { provide } from "../../../helpers/wrap-and-provide";
import { FormTextField } from "../../form-components/form-text-field/FormTextField";
import { UploadImage } from "../../form-components/image-uploader/ImageUploader";

interface CommunityCardProps {
  isOpen: boolean;
  onClose: VoidFunction;
  isEdit: boolean;
  communityDetails?: Community;
  onComplete?: VoidFunction;
}

const validationSchema = yup.object().shape({
  name: yup
    .string()
    .trim()
    .min(2, "שם הקהילה חייב להכיל לפחות 2 תווים")
    .required("שדה זה הינו חובה"),
  description: yup.string(),
  joinProcessText: yup.string(),
});

const COMMUNITIES_OPTIONS_QUERY_KEY = "admin-communities-options";

const CommunityCard: FC<CommunityCardProps> = ({
  isOpen,
  onClose,
  isEdit,
  communityDetails,
  onComplete,
}) => {
  const { currOrganization } = useOrganization();
  const queryClient = useQueryClient();
  const { setAlertText, setSeverity } = useAlert();
  const { values, setValues, isValid, resetForm } = useForm<{
    name: string;
    description: string;
    joinProcessText: string;
  }>();
  const [parentCommunity, setParentCommunity] = useState<Community | null>(null);
  const [joinProcessLogoImageId, setJoinProcessLogoImageId] = useState("");
  const [shortRegistration, setShortRegistration] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { data: allCommunities = [], isLoading: communitiesLoading } = useQuery({
    queryKey: [COMMUNITIES_OPTIONS_QUERY_KEY, currOrganization?.id],
    enabled: isOpen && !!currOrganization?.id,
    queryFn: () =>
      communityRepo.find({
        where: { organizationId: currOrganization!.id },
        orderBy: { name: "asc" },
      }),
  });

  const parentOptions = useMemo(
    () =>
      getParentCommunityOptions(
        allCommunities,
        isEdit ? communityDetails?.id : undefined,
      ),
    [allCommunities, communityDetails?.id, isEdit],
  );

  const registrationUrl =
    isEdit && communityDetails?.id
      ? communityJoinUrl(communityDetails.id)
      : null;

  // טוען פרטי עריכה פעם אחת לפתיחת דיאלוג — לא לכלול setValues ב-deps (נוצר מחדש כל רינדור)
  useEffect(() => {
    if (!isOpen || !isEdit || !communityDetails) return;

    setValues({
      name: communityDetails.name ?? "",
      description: communityDetails.description ?? "",
      joinProcessText: communityDetails.joinProcessText ?? "",
    });
    setJoinProcessLogoImageId(communityDetails.joinProcessLogoImageId ?? "");
    setShortRegistration(communityDetails.shortRegistration === true);
  }, [isOpen, isEdit, communityDetails?.id]);

  useEffect(() => {
    if (!isOpen || !isEdit || !communityDetails) return;

    if (!communityDetails.parentCommunityId) {
      setParentCommunity(null);
      return;
    }

    const parent =
      parentOptions.find(
        (community) => community.id === communityDetails.parentCommunityId,
      ) ?? null;

    setParentCommunity((prev) => (prev?.id === parent?.id ? prev : parent));
  }, [
    isOpen,
    isEdit,
    communityDetails?.id,
    communityDetails?.parentCommunityId,
    parentOptions,
  ]);

  const handleClose = () => {
    resetForm();
    setParentCommunity(null);
    setJoinProcessLogoImageId("");
    setShortRegistration(false);
    onClose();
  };

  const copyRegistrationUrl = () => {
    if (!registrationUrl) return;
    navigator.clipboard.writeText(registrationUrl);
    setAlertText("קישור ההרשמה הועתק ללוח");
    setSeverity("success");
  };

  const handleSave = async () => {
    if (!currOrganization?.id || !isValid) return;

    setIsSaving(true);
    try {
      const payload = {
        name: values.name.trim(),
        description: values.description?.trim() ?? "",
        joinProcessText: values.joinProcessText?.trim() ?? "",
        joinProcessLogoImageId: joinProcessLogoImageId.trim() || undefined,
        shortRegistration,
        organizationId: currOrganization.id,
        parentCommunityId: parentCommunity?.id ?? "",
      };

      if (isEdit && communityDetails) {
        await communityRepo.update(communityDetails.id, payload);
        setAlertText("הקהילה עודכנה בהצלחה");
      } else {
        await communityRepo.insert(payload);
        setAlertText("הקהילה נוצרה בהצלחה");
      }

      await queryClient.invalidateQueries({
        queryKey: [COMMUNITIES_OPTIONS_QUERY_KEY],
      });
      setSeverity("success");
      onComplete?.();
      handleClose();
    } catch {
      setAlertText("שגיאה בשמירת הקהילה");
      setSeverity("error");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogContent>
        <Typography variant="h5" color="secondary" sx={{ mb: 3, fontWeight: "bold" }}>
          {isEdit ? "עריכת קהילה" : "הוספת קהילה"}
        </Typography>
        <Stack spacing={2}>
          <FormTextField name="name" label="שם הקהילה" fullWidth />
          <FormTextField
            name="description"
            label="תיאור"
            fullWidth
            multiline
            minRows={3}
          />
          <Typography variant="subtitle2" color="text.secondary">
            מסך הסבר בקישור הצטרפות לקהילה (לוגו + טקסט)
          </Typography>
          <UploadImage
            name="joinProcessLogoImageId"
            imageId={joinProcessLogoImageId}
            handleImageChange={setJoinProcessLogoImageId}
          />
          <FormTextField
            name="joinProcessText"
            label="טקסט הסבר על תהליך ההצטרפות"
            fullWidth
            multiline
            minRows={4}
            helperText="יוצג למתנדבים בקישור ההרשמה לפני ההתחברות או ההצטרפות."
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={shortRegistration}
                onChange={(_, checked) => setShortRegistration(checked)}
              />
            }
            label="הרשמה מקוצרת (שם, טלפון וקוד). מגדר, תאריך לידה ות.ז. נדרשים רק בהרשמה להתנדבות."
          />
          <Autocomplete
            options={parentOptions}
            loading={communitiesLoading}
            value={parentCommunity}
            onChange={(_, value) => setParentCommunity(value)}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField {...params} label="קהילת אם (אופציונלי)" />
            )}
          />
          {registrationUrl && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <TextField
                label="קישור הרשמה למתנדבים"
                value={registrationUrl}
                fullWidth
                InputProps={{ readOnly: true }}
              />
              <IconButton
                aria-label="העתק קישור הרשמה"
                onClick={copyRegistrationUrl}
              >
                <ContentCopyIcon />
              </IconButton>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isSaving}>
          ביטול
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!isValid || isSaving}
        >
          {isSaving ? <CircularProgress size={22} color="inherit" /> : "שמירה"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default provide([
  FormProvider,
  {
    initialValues: { name: "", description: "", joinProcessText: "" },
    validationSchema,
    className: "community-form",
    onSubmit: () => undefined,
  },
])(CommunityCard);
